import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Home, Mail, MapPin, Phone, RefreshCw, UserRound, X } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import api from "@/lib/api";
import { isAdmin, useAuth } from "@/lib/auth.jsx";
import { getApiErrorMessage } from "@/lib/apiError";

function fullName(intake) {
    const contact = intake?.contact || {};
    const firstLast = [contact.first_name, contact.last_name].filter(Boolean).join(" ");
    return (
        intake?.customer_name ||
        intake?.full_name ||
        contact.customer_name ||
        contact.full_name ||
        firstLast ||
        contact.name ||
        contact.phone ||
        "Unnamed lead"
    );
}

function locationText(intake) {
    const property = intake?.property || {};
    const contact = intake?.contact || {};
    const city = property.city || contact.city;
    const state = property.state || contact.state;
    return [city, state].filter(Boolean).join(", ") || "Property location not provided";
}

function statusLabel(value) {
    if (value === "needs_review") return "Needs review";
    if (value === "in_progress") return "Quote started";
    return "Ready";
}

function statusClass(value) {
    if (value === "needs_review") return "border-amber-200 bg-amber-50 text-amber-800";
    if (value === "in_progress") return "border-sky-200 bg-sky-50 text-sky-800";
    return "border-emerald-200 bg-emerald-50 text-emerald-800";
}

export default function ReadyToQuote() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const canViewDiagnostics = isAdmin(user);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [workingId, setWorkingId] = useState(null);
    const [importingGhl, setImportingGhl] = useState(false);
    const [importDiagnostics, setImportDiagnostics] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const response = await api.get("/quote-intakes");
            setItems(Array.isArray(response.data) ? response.data : []);
        } catch (error) {
            toast.error(getApiErrorMessage(error, "Unable to load quote-ready leads"));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const counts = useMemo(() => ({
        total: items.length,
        ready: items.filter((item) => item.status === "ready").length,
        review: items.filter((item) => item.status === "needs_review").length,
        started: items.filter((item) => item.status === "in_progress").length,
    }), [items]);

    const prepare = async (item) => {
        setWorkingId(item.id);
        try {
            const response = await api.post(`/quote-intakes/${item.id}/prepare-draft`);
            const quoteId = response.data?.quote_id;
            if (!quoteId) throw new Error("Quote creation did not return an ID");
            navigate(`/quotes/${encodeURIComponent(quoteId)}/edit`);
        } catch (error) {
            toast.error(getApiErrorMessage(error, "Unable to create a quote from this lead"));
        } finally {
            setWorkingId(null);
        }
    };

    const importGhlLeads = async () => {
        setImportingGhl(true);
        setImportDiagnostics(null);
        try {
            const response = await api.post("/integrations/ghl/import-leads");
            const data = response.data || {};
            const errors = Array.isArray(data.errors) ? data.errors.length : 0;
            const imported = data.imported || 0;
            const updated = data.updated_existing || 0;
            const reimported = data.reimported_deleted || 0;
            const skipped = data.skipped_active_duplicates ?? data.skipped_duplicates ?? 0;
            if (imported > 0 || updated > 0 || reimported > 0 || skipped > 0) {
                toast.success(`Imported ${imported}, updated ${updated}, re-imported ${reimported}, skipped ${skipped} active duplicates.${errors ? ` ${errors} error${errors === 1 ? "" : "s"}.` : ""}`);
                await load();
            } else if (errors) {
                toast.error("Unable to import GHL contacts right now.");
            } else {
                toast.info(data.message || "No GHL contacts are tagged quote_ready.");
                await load();
            }
        } catch (error) {
            const detail = error.response?.data?.detail;
            const diagnosticPayload = typeof detail === "object" ? detail : null;
            if (canViewDiagnostics && diagnosticPayload?.diagnostics) {
                setImportDiagnostics(diagnosticPayload);
            }
            if (detail === "GHL integration is not configured.") {
                toast.error("GHL integration is not configured.");
            } else if (diagnosticPayload?.error) {
                toast.error(diagnosticPayload.error);
            } else {
                toast.error(getApiErrorMessage(error, "Unable to import GHL contacts right now."));
            }
        } finally {
            setImportingGhl(false);
        }
    };

    const dismiss = async (item) => {
        if (!window.confirm(`Dismiss ${fullName(item)} from the quote queue?`)) return;
        setWorkingId(item.id);
        try {
            await api.post(`/quote-intakes/${item.id}/dismiss`);
            setItems((current) => current.filter((candidate) => candidate.id !== item.id));
            toast.success("Lead removed from the quote queue");
        } catch (error) {
            toast.error(getApiErrorMessage(error, "Unable to dismiss this lead"));
        } finally {
            setWorkingId(null);
        }
    };

    return (
        <div className="mx-auto w-full max-w-[1180px] space-y-6" data-testid="ready-to-quote-page">
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <div className="text-xs uppercase tracking-wider text-slate-500">GHL imports</div>
                    <h1 className="font-display text-3xl text-slate-900">Ready to Quote</h1>
                    <p className="mt-1 max-w-2xl text-sm text-slate-600">
                        Ready-to-quote leads arrive here from GHL contacts tagged quote_ready by the GHL workflow.
                    </p>
                </div>
                <div className="flex flex-wrap gap-2">
                    <Button variant="outline" onClick={importGhlLeads} disabled={importingGhl || loading} data-testid="ready-to-quote-import-ghl-button">
                        <RefreshCw className={`mr-2 h-4 w-4 ${importingGhl ? "animate-spin" : ""}`} /> {importingGhl ? "Importing…" : "Import Quote Ready GHL Contacts"}
                    </Button>
                    <Button variant="outline" onClick={load} disabled={loading || importingGhl} data-testid="ready-to-quote-refresh-button">
                        <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
                    </Button>
                </div>
            </div>


            {canViewDiagnostics && importDiagnostics?.diagnostics && (
                <details className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950" data-testid="ghl-import-diagnostics">
                    <summary className="cursor-pointer font-semibold">GHL import diagnostics</summary>
                    <p className="mt-2">{importDiagnostics.error || "GHL import failed."}</p>
                    <pre className="mt-3 max-h-80 overflow-auto rounded-lg bg-white/80 p-3 text-xs text-slate-800">
                        {JSON.stringify(importDiagnostics.diagnostics, null, 2)}
                    </pre>
                </details>
            )}

            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                {[
                    ["Active", counts.total],
                    ["Ready", counts.ready],
                    ["Needs review", counts.review],
                    ["Started", counts.started],
                ].map(([label, value]) => (
                    <Card key={label} className="rounded-xl border-slate-200 p-4">
                        <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
                        <div className="mt-1 text-2xl font-semibold text-slate-900">{value}</div>
                    </Card>
                ))}
            </div>

            {loading ? (
                <Card className="rounded-xl border-slate-200 p-8 text-center text-sm text-slate-500">Loading imported leads…</Card>
            ) : items.length === 0 ? (
                <Card className="rounded-xl border-dashed border-slate-300 p-10 text-center">
                    <UserRound className="mx-auto h-8 w-8 text-slate-400" />
                    <h2 className="mt-3 text-lg font-semibold text-slate-900">No leads are waiting for a quote</h2>
                    <p className="mt-1 text-sm text-slate-500">No GHL contacts are tagged quote_ready.</p>
                </Card>
            ) : (
                <div className="grid gap-4 lg:grid-cols-2">
                    {items.map((item) => {
                        const contact = item.contact || {};
                        const preferences = item.preferences || {};
                        const missing = item.missing_fields || [];
                        const blockingMissing = missing.filter((field) => !["contact.first_name", "contact.last_name"].includes(field));
                        const warnings = [...(item.warnings || [])];
                        if ((missing.includes("contact.first_name") || missing.includes("contact.last_name")) && !warnings.includes("Customer name incomplete")) {
                            warnings.push("Customer name incomplete");
                        }
                        const disabled = workingId === item.id || blockingMissing.length > 0;
                        return (
                            <Card key={item.id} className="rounded-xl border-slate-200 p-5" data-testid={`quote-intake-card-${item.id}`}>
                                <div className="flex items-start justify-between gap-4">
                                    <div className="min-w-0">
                                        <div className="flex flex-wrap items-center gap-2">
                                            <h2 className="truncate text-lg font-semibold text-slate-900">{fullName(item)}</h2>
                                            <span className={`rounded-full border px-2 py-0.5 text-xs font-medium ${statusClass(item.status)}`}>
                                                {statusLabel(item.status)}
                                            </span>
                                        </div>
                                        <div className="mt-1 text-xs text-slate-500">
                                            Assigned to {item.assigned_user_name || item.assigned_user_email || "manager queue"}
                                        </div>
                                    </div>
                                    <Button variant="ghost" size="icon" onClick={() => dismiss(item)} disabled={workingId === item.id} aria-label="Dismiss lead">
                                        <X className="h-4 w-4" />
                                    </Button>
                                </div>

                                <div className="mt-4 grid gap-2 text-sm text-slate-700 sm:grid-cols-2">
                                    <div className="flex items-center gap-2"><Phone className="h-4 w-4 text-slate-400" /> {contact.phone || "No phone"}</div>
                                    <div className="flex items-center gap-2"><Mail className="h-4 w-4 text-slate-400" /> <span className="truncate">{contact.email || "No email"}</span></div>
                                    <div className="flex items-center gap-2"><MapPin className="h-4 w-4 text-slate-400" /> {locationText(item)}</div>
                                    <div className="flex items-center gap-2"><Home className="h-4 w-4 text-slate-400" /> {preferences.home_model || "Home not selected"}</div>
                                </div>

                                {blockingMissing.length > 0 && (
                                    <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                                        This import is missing a contact method: phone or email. Add one before creating a quote.
                                    </div>
                                )}

                                {warnings.length > 0 && (
                                    <div className="mt-4 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-900">
                                        {warnings.join(", ")}. You can still create a quote when phone or email is present.
                                    </div>
                                )}

                                {(item.lead?.notes || item.lead?.purchase_timeframe) && (
                                    <div className="mt-4 rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-600">
                                        {item.lead?.purchase_timeframe && <div><strong>Timeframe:</strong> {item.lead.purchase_timeframe}</div>}
                                        {item.lead?.notes && <div className="mt-1"><strong>Notes:</strong> {item.lead.notes}</div>}
                                    </div>
                                )}

                                <div className="mt-5 flex justify-end">
                                    <Button
                                        onClick={() => prepare(item)}
                                        disabled={disabled}
                                        className="bg-[#0F2A47] text-white hover:bg-[#14385E]"
                                        data-testid={`quote-intake-create-quote-${item.id}`}
                                    >
                                        {workingId === item.id ? "Preparing…" : item.status === "in_progress" ? "Resume Quote" : "Create Quote"}
                                    </Button>
                                </div>
                            </Card>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
